'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTransactionQueue, StepStatus } from '@/hooks/use-transactions';
import { X, Check, Loader2, AlertCircle, ExternalLink, RotateCcw, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { playFailedSound } from '@/lib/sounds';

// Chain explorer URL
const chainId = process.env.NEXT_PUBLIC_CHAIN_ID;
const explorerUrl = chainId === '8453' 
  ? 'https://basescan.org' 
  : 'https://sepolia.basescan.org';

// Auto-close delay in ms after all steps complete
const AUTO_CLOSE_DELAY = 3000;

function isUserRejection(msg: string): boolean {
  const lower = msg.toLowerCase();
  return lower.includes('rejected') || lower.includes('denied') ||
    lower.includes('cancelled') || lower.includes('canceled') ||
    lower.includes('user refused');
}

// Animation duration in ms
const ANIMATION_DURATION = 150;

function StatusBadge({ status, isRetrying, hash }: { status: StepStatus; isRetrying?: boolean; hash?: string }) {
  if (isRetrying && status === 'waiting') {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 border border-amber-400/35">
        Retrying...
      </span>
    );
  }

  const labels: Record<StepStatus, string> = {
    waiting: 'Waiting',
    pending: 'Pending',
    success: 'Complete',
    error: 'Failed',
    cancelled: 'Cancelled',
  };

  // For success status with a hash, render as a link to basescan
  if (status === 'success' && hash) {
    return (
      <a
        href={`${explorerUrl}/tx/${hash}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-400/35 hover:bg-emerald-500/25 transition-colors"
      >
        {labels[status]}
        <ExternalLink className="h-3 w-3" />
      </a>
    );
  }

  return (
    <span className={cn(
      'text-xs px-1.5 py-0.5 rounded',
      status === 'waiting' && 'bg-zinc-800/80 text-zinc-400 border border-zinc-600/40',
      status === 'pending' && 'bg-zinc-800/80 text-zinc-300 border border-zinc-600/40',
      status === 'success' && 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/35',
      status === 'error' && 'bg-rose-500/15 text-rose-300 border border-rose-400/35',
      status === 'cancelled' && 'bg-zinc-800/80 text-zinc-500 border border-zinc-600/40',
    )}>
      {labels[status]}
    </span>
  );
}

export function TransactionSnackbar() {
  const { state, close, retryFromFailed, retryAll, cancelQueue, canRetry } = useTransactionQueue();
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isEntering, setIsEntering] = useState(true);

  const allComplete = state.steps.length > 0 && state.steps.every(s => s.status === 'success');
  const hasError = state.steps.some(s => s.status === 'error');
  const hasCancelled = state.steps.some(s => s.status === 'cancelled');
  const isProcessing = state.steps.length > 1
    && !allComplete && !hasError && !hasCancelled
    && state.steps.some(s => s.status === 'pending' || s.status === 'success');

  // Play failed sound when an error is first detected
  const prevHasErrorRef = useRef(false);
  useEffect(() => {
    if (hasError && !prevHasErrorRef.current) {
      playFailedSound();
    }
    prevHasErrorRef.current = hasError;
  }, [hasError]);

  // Detect if we're in batch mode: all steps have the batched flag
  const isBatchMode = useMemo(
    () => state.steps.length > 1 && state.steps.every(s => s.batched),
    [state.steps],
  );

  const totalChunks = state.totalBatchChunks || 1;
  const isMultiBatch = totalChunks > 1;

  // Derive overall batch status from individual step statuses
  const batchStatus: StepStatus = useMemo(() => {
    if (!isBatchMode) return 'waiting';
    if (state.steps.some(s => s.status === 'error')) return 'error';
    if (state.steps.every(s => s.status === 'success')) return 'success';
    if (state.steps.some(s => s.status === 'pending')) return 'pending';
    return 'waiting';
  }, [isBatchMode, state.steps]);

  const batchHash = isBatchMode ? state.steps.find(s => s.hash)?.hash : undefined;
  const batchError = isBatchMode ? state.steps.find(s => s.error)?.error : undefined;

  // Handle open state changes with animation
  useEffect(() => {
    if (state.isOpen && !isVisible && !isExiting) {
      setIsEntering(true);
      setIsVisible(true);
      // Trigger slide-in on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsEntering(false);
        });
      });
    }
  }, [state.isOpen, isVisible, isExiting]);

  // Animated close handler
  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      close();
      setIsVisible(false);
      setIsExiting(false);
    }, ANIMATION_DURATION);
  };

  // Auto-close after all steps complete successfully (but not while hovering)
  useEffect(() => {
    if (allComplete && state.isOpen && !isHovered) {
      autoCloseTimerRef.current = setTimeout(() => {
        handleClose();
      }, AUTO_CLOSE_DELAY);
    }

    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, [allComplete, state.isOpen, isHovered]);

  // Clear timer when hovering starts
  useEffect(() => {
    if (isHovered && autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  }, [isHovered]);

  if (!state.isOpen && !isVisible) return null;

  return createPortal(
    <div 
      className={cn(
        "fixed bottom-4 right-4 z-[100] w-80 max-w-[calc(100vw-2rem)] pointer-events-none",
        "transition-all ease-out",
        (isExiting || isEntering)
          ? "translate-x-[calc(100%+1rem)] opacity-0" 
          : "translate-x-0 opacity-100"
      )}
      style={{ transitionDuration: `${ANIMATION_DURATION}ms` }}
    >
      <div
        data-transaction-snackbar
        className="bg-[#0b0a08]/95 backdrop-blur-md border border-amber-500/30 rounded-md shadow-[0_20px_50px_-20px_rgba(0,0,0,0.9)] overflow-hidden pointer-events-auto text-zinc-100"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-amber-500/20 bg-black/40">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-medium text-sm truncate text-amber-100/95">{state.title}</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-zinc-500 hover:text-amber-100/90 transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Condensed batch view — single row for all batched steps */}
        {isBatchMode ? (
          <div className={cn(
            'px-4 py-3 transition-colors',
            batchStatus === 'pending' && 'bg-amber-500/5',
            batchStatus === 'success' && 'bg-emerald-500/5',
            batchStatus === 'error' && 'bg-rose-500/5',
          )}>
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center shrink-0',
                batchStatus === 'waiting' && 'bg-zinc-800 text-zinc-400 border border-zinc-600/50',
                batchStatus === 'pending' && 'bg-amber-500 text-zinc-950',
                batchStatus === 'success' && 'bg-emerald-500 text-zinc-950',
                batchStatus === 'error' && 'bg-rose-500 text-white',
              )}>
                {batchStatus === 'success' ? (
                  <Check className="h-3.5 w-3.5" />
                ) : batchStatus === 'error' ? (
                  <AlertCircle className="h-3.5 w-3.5" />
                ) : batchStatus === 'pending' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Zap className="h-3.5 w-3.5" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-sm truncate text-zinc-100">
                    {state.title}
                  </p>
                  {batchStatus === 'success' && batchHash ? (
                    <a
                      href={`${explorerUrl}/tx/${batchHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-400/35 hover:bg-emerald-500/25 transition-colors shrink-0"
                    >
                      <Zap className="h-3 w-3" />
                      Batched
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : batchStatus === 'pending' ? (
                    <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 shrink-0">
                      <Zap className="h-3 w-3" />
                      Signing...
                    </span>
                  ) : batchStatus === 'error' ? (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-400/35 shrink-0">
                      Failed
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {state.steps.length} transaction{state.steps.length !== 1 ? 's' : ''} in {totalChunks} batch{totalChunks !== 1 ? 'es' : ''}
                  {isMultiBatch && batchStatus === 'pending' && state.batchedCount > 0 && (
                    <> &middot; {state.batchedCount}/{state.steps.length} confirmed</>
                  )}
                </p>
                {batchError && (
                  <p className="text-xs text-rose-300 mt-1 truncate">
                    {isUserRejection(batchError) ? 'Transaction rejected by user' : batchError}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Sequential step list — scrollable when many steps */
          <div className="divide-y divide-amber-500/15 max-h-[60vh] overflow-y-auto">
            {state.steps.map((step, index) => (
              <div
                key={step.id}
                className={cn(
                  'px-4 py-3 transition-colors',
                  step.status === 'pending' && 'bg-amber-500/5',
                  step.status === 'success' && 'bg-emerald-500/5',
                  step.status === 'error' && 'bg-rose-500/5',
                )}
              >
                <div className="flex items-start gap-3">
                  {/* Step number and status */}
                  <div className="flex flex-col items-center gap-1">
                    <div className={cn(
                      'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium',
                      step.status === 'waiting' && 'bg-zinc-800 text-zinc-400 border border-zinc-600/50',
                      step.status === 'pending' && 'bg-amber-500/30 text-amber-100 border border-amber-400/40',
                      step.status === 'success' && 'bg-emerald-500 text-zinc-950',
                      step.status === 'error' && 'bg-rose-500 text-white',
                      step.status === 'cancelled' && 'bg-zinc-800 text-zinc-500 border border-zinc-600/50',
                    )}>
                      {step.status === 'success' ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : step.status === 'error' ? (
                        <AlertCircle className="h-3.5 w-3.5" />
                      ) : step.status === 'pending' ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    {/* Connector line */}
                    {index < state.steps.length - 1 && (
                      <div className={cn(
                        'w-0.5 h-4 -mb-1',
                        step.status === 'success' ? 'bg-emerald-500/50' : 'bg-zinc-700/80',
                      )} />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm truncate text-zinc-100">{step.label}</p>
                      <StatusBadge status={step.status} isRetrying={state.isRetrying} hash={step.hash} />
                    </div>
                    
                    {step.description && (
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {step.description}
                      </p>
                    )}

                    {step.error && (
                      <p className="text-xs text-rose-300 mt-1 truncate">
                        {isUserRejection(step.error) ? 'Transaction rejected by user' : step.error}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer - Progress summary and retry buttons */}
        <div className="px-4 py-2 bg-black/40 border-t border-amber-500/20">
          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-500">
              {isBatchMode
                ? `${state.steps.length} txn${state.steps.length !== 1 ? 's' : ''} in ${totalChunks} batch${totalChunks !== 1 ? 'es' : ''}`
                : `${state.steps.filter(s => s.status === 'success').length} of ${state.steps.length} complete`
              }
            </span>
            {allComplete && isBatchMode && (
              <span className="text-emerald-300 font-medium flex items-center gap-1">
                <Zap className="h-3 w-3" />
                Batched!
              </span>
            )}
            {allComplete && !isBatchMode && (
              <span className="text-emerald-300 font-medium">All complete!</span>
            )}
            {hasCancelled && (
              <span className="text-zinc-500 font-medium">Cancelled</span>
            )}
            {isProcessing && !state.isRetrying && (
              <button
                onClick={cancelQueue}
                className="text-zinc-500 hover:text-amber-100/90 font-medium transition-colors"
              >
                Cancel
              </button>
            )}
            {state.isRetrying && (
              <span className="text-amber-200 font-medium flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Retrying...
              </span>
            )}
          </div>
          
          {/* Retry + Close buttons on error */}
          {canRetry && (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-amber-500/15">
              <button
                onClick={() => retryFromFailed()}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gradient-to-b from-amber-400 to-amber-500 border border-amber-300/60 text-black hover:brightness-110 transition-[filter]"
              >
                <RotateCcw className="h-3 w-3" />
                Retry
              </button>
              <button
                onClick={() => retryAll()}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
                Retry All
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
